import ActionSheet from '@anm/components/modals/ActionSheet';
import { DEFAULT_VIDEO_NAME } from '@anm/constants/my-videos';
import getPopoverUrl from '@anm/helpers/getPopoverUrl';
import isMobile from '@anm/helpers/is/isMobile';
import isMobileOrIPad from '@anm/helpers/is/isMobileOrIPad';
import isStreamVideo from '@anm/helpers/is/isStreamVideo';
import useDownloadStreamVideo from '@anm/hooks/useDownloadStreamVideo';
import { VideoSizes } from '@anm/hosting-player';
import Head from 'next/head';
import { useCallback, FC } from 'react';
import { useSelector } from 'react-redux';

import useFetchUserPrefs from '../../../hooks/useFetchUserPrefs';
import { hostedVideoSelectors } from '../../../store/modules/hostedVideo';
import { projectSelectors } from '../../../store/modules/project';
import { streamSelectors } from '../../../store/modules/stream';
import { userSelectors } from '../../../store/modules/user';
import ProjectFolderTreeModalProvider from '../..//my-projects/ProjectPlayerBlock/ProjectFolderTreeModalProvider';
import useFetchFolder from '../../my-projects/VideoFolders/hooks/useFetchFolder';
import { useDownloadOptionsMobile } from '../DownloadVideo/DownloadDropdown';
import InfoMobileModal from '../DownloadVideo/InfoMobileModal';

import Header from './Header';
import Layout from './Layout';
import Notifications from './Notifications';
import RightPanel from './RightPanel';
import SaveBlock from './SaveBlock';
import { LoaderWrapper, Wrapper } from './Wrapper';
import useCustomizationSectionInit from './hooks/useCustomizationSectionInit';
import useHandlers from './hooks/useHandlers';
import usePaidThemeAndLayout from './hooks/usePaidThemeAndLayout';
import useRedirectToError from './hooks/useRedirectToError';
import useRemovedProject from './hooks/useRemovedProject';
import useUploadCheck from './hooks/useUploadCheck';
import { useFetchBranding, usePrefferedBrandId } from './tabs/LandingTab/hooks';
import useFetchDomains from '../../webinar/DomainsTab/hooks/useFetchDomains';
import {
  BROADCASTS_TAB,
  CustomizationTabIndexes,
  DOMAINS_TAB,
  EMBED_TAB,
  LANDING_TAB,
  STATS_TAB
} from '../../../pages/video';
import { uploadsSelectors } from '@anm/store/modules/uploads';
import useFetchProjectById from './hooks/useFetchProjectById';
import useFolderTree from '../../../hooks/useFolderTree';
import { AnimatronProject } from 'types/project';
import useProjectsPermissions from '../../my-projects/hooks/useProjectsPermissions';

export * from './Wrapper';

type CustomizationSectionCommonProps = {
  activeTab: CustomizationTabIndexes;
  onTabChange: (tab: CustomizationTabIndexes) => void;
};

export type CustomizationSectionProps = SectionWrapperProps & {
  hostId: string;
};

export const DOWNLOAD_BUTTON_LABEL = 'Download';

type SectionWrapperProps = CustomizationSectionCommonProps & {
  projectId: string;
};

type WrapperProps = SectionWrapperProps & {
  project: AnimatronProject;
};

const showNavigationDropdownBreakpoint = 1023;

const CustomizationSection: FC<CustomizationSectionProps> = props => {
  const { canEditProject } = useProjectsPermissions();
  const [
    errorCode,
    isMounted,
    currentTime,
    errorMessage,
    hostingUserBlockedStatus,
    setCurrentTime
  ] = useCustomizationSectionInit(props);

  const uploadId = useSelector(streamSelectors.selectStreamUploadId);
  const handleDownloadStream = useDownloadStreamVideo(uploadId);

  const [
    showInfoOnMobile,
    isActionSheetOpen,
    actionSheetOptions,
    setShowInfoOnMobile,
    setIsActionSheetOpen,
    handleActionSheetChange
  ] = useDownloadOptionsMobile({ onStreamDownload: handleDownloadStream });

  const { shouldUpgrade, upgradeDescription } = usePaidThemeAndLayout();

  const videoData = useSelector(hostedVideoSelectors.selectVideoData);
  const canUserDownloadVideo = useSelector(userSelectors.selectCanUserDownloadVideo);
  const videoName = useSelector(projectSelectors.selectCurrentProjectName) || DEFAULT_VIDEO_NAME;

  const handleInfoModalClose = useCallback(() => setShowInfoOnMobile(false), []);
  const handleActionSheetClose = useCallback(() => setIsActionSheetOpen(false), []);
  const handleActionSheetOpen = useCallback(() => setIsActionSheetOpen(true), []);

  const projectSizes = useSelector(projectSelectors.selectCurrentProjectSizes) as VideoSizes;

  useRedirectToError(errorCode);

  const [wasChanged, getHandlers] = useHandlers(props.hostId);

  const canNotify = useSelector(hostedVideoSelectors.selectNotificationsStatus);

  if (!videoData || typeof hostingUserBlockedStatus === 'undefined') return <LoaderWrapper size="large" color="blue" />;

  const { hostId, projectId, activeTab: activeButton, onTabChange } = props;

  const isStatsTab = activeButton === STATS_TAB;
  const isBroadcastsTab = activeButton === BROADCASTS_TAB;
  const isDomainsTab = activeButton === DOMAINS_TAB;
  const isSimplifiedOnMobileTab = isMobile() && activeButton === EMBED_TAB;

  const showRightPanel = !isStatsTab && !isSimplifiedOnMobileTab && !isBroadcastsTab && !isDomainsTab;

  const { sizes: videoSizes, bestQualityVideo: videoUrl } = videoData;

  const sizes = videoSizes?.width ? videoSizes : projectSizes;

  const isStream = actionSheetOptions.some(v => isStreamVideo(v.value));
  const canShowActionSheet = isActionSheetOpen && (isStream || canUserDownloadVideo) && actionSheetOptions.length;

  return (
    <ProjectFolderTreeModalProvider>
      {canNotify && <Notifications />}
      {showInfoOnMobile && isMobile() && <InfoMobileModal onModalClose={handleInfoModalClose} />}
      {canShowActionSheet && (
        <ActionSheet
          title={{
            text: 'Download video and audio',
            icon: 'images/icons/download_icon_black.svg'
          }}
          options={actionSheetOptions}
          onChange={handleActionSheetChange}
          onModalClose={handleActionSheetClose}
        />
      )}
      <Header {...{ hostId, projectId }} />
      <Wrapper isLandingScreen={activeButton === LANDING_TAB}>
        {canEditProject && (
          <>
            {!isMobileOrIPad() && canNotify && (
              <SaveBlock {...{ wasChanged, getHandlers, shouldUpgrade, upgradeDescription }} />
            )}
            <Layout
              {...{
                sizes,
                hostId,
                videoUrl,
                errorCode,
                videoName,
                projectId,
                wasChanged,
                getHandlers,
                activeButton,
                currentTime,
                showNavigationDropdownBreakpoint
              }}
              onActionSheetOpen={handleActionSheetOpen}
              onActiveButtonChange={onTabChange}
              onDownloadStream={handleDownloadStream}
            />
          </>
        )}
        {isMounted && showRightPanel && (
          <RightPanel
            {...{
              sizes,
              hostId,
              projectId,
              errorCode,
              wasChanged,
              getHandlers,
              errorMessage,
              activeButton,
              setCurrentTime
            }}
          />
        )}
      </Wrapper>
    </ProjectFolderTreeModalProvider>
  );
};

const SectionWrapper: FC<WrapperProps> = ({ project, ...props }) => {
  useFetchBranding();
  useFetchUserPrefs();
  usePrefferedBrandId();
  useRemovedProject();
  useFetchDomains();

  const isFolderPending = useFetchFolder(project.folderId);

  const [currentProject, isUploadExists] = useUploadCheck({
    projectId: props.projectId
  });

  const createdHostId = useSelector(uploadsSelectors.selectCreatedHostId);
  const hostId = currentProject?.extra.hostingId || project.extra.hostingId || createdHostId;

  const canRenderSection = isUploadExists && !!hostId && !isFolderPending;

  return (
    <>
      <Head key="video-page">
        <script src={getPopoverUrl()}></script>
      </Head>
      {canRenderSection ? (
        <CustomizationSection hostId={hostId!} {...props} />
      ) : (
        <LoaderWrapper size="large" color="blue" />
      )}
    </>
  );
};

const FetchFolderWrapper: FC<SectionWrapperProps> = props => {
  const [project, isPending] = useFetchProjectById(props.projectId);
  const tree = useFolderTree();

  if (isPending || !project || !tree) return <LoaderWrapper size="large" color="blue" />;

  return <SectionWrapper {...props} project={project} />;
};

export default FetchFolderWrapper;
